import type {
  ElectionOutcomeDto,
  RecordElectionOutcomeRequest,
} from '@signalvote/shared';
import { apiJson } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { refusal } from './useFinanceContributions';
import { FINANCE_STALE_TIME } from './queryUtils';
import { useRefreshFinanceAfter } from './useFinanceSettings';

// ADR-076 (#630) — the CURRENT campaign's per-stage election outcomes plus the recording mutation,
// backing the owner/platform-admin recording control. A read (one row per recorded stage, each
// carrying its concurrency token) plus a POST that upserts one stage. The fresh token arrives with
// the refetch the write set starts.

export type { ElectionOutcomeDto, RecordElectionOutcomeRequest };

/**
 * GET /api/finance/election-outcomes/current — the current outcome recorded for each stage of the
 * selected campaign (owner / platform-admin; the server 403s anyone else).
 */
const campaignElectionOutcomesRead = defineQuery({
  key: () => ['finance', 'election-outcomes', 'current'],
  reads: 'GET /api/finance/election-outcomes/current',
  staleTime: FINANCE_STALE_TIME,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<ElectionOutcomeDto[]>('/api/finance/election-outcomes/current', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch election outcomes',
    }),
});

export function useCampaignElectionOutcomes() {
  return campaignElectionOutcomesRead(undefined);
}

/**
 * POST /api/finance/election-outcomes — record what happened at a stage (upsert, one row per stage).
 * Its write set refreshes the outcomes, the finance settings and the deadlines. A runoff date also
 * moves donor totals and recurring cutoffs that report previews and the payments reads show, which
 * are not defined reads yet, so it refreshes the undeclared finance reads too. A lost race fails as
 * `stale` with the server's sentence.
 */
const recordElectionOutcome = defineWrite({
  send: (request: RecordElectionOutcomeRequest) =>
    apiJson<ElectionOutcomeDto>('/api/finance/election-outcomes', {
      method: 'POST',
      json: request,
      error: refusal('Failed to record the election outcome'),
    }),
});

export function useRecordElectionOutcome() {
  return useRefreshFinanceAfter(recordElectionOutcome());
}
